import React, { Suspense, useState } from 'react';
import cx from 'classnames';
import { useQueryClient } from 'react-query';

import AddCreditCardForm from '~/components/account/settings/add-credit-card-form';
import Button from '~/components/button';
import LoadingIndicator from '~/components/loading-indicator';
import TermsConsent from '~/components/cart/terms-consent';
import Modal from '~/components/modals/modal';
import { CreditCardBanner } from '~/components/modals/account-settings/update-payment-modal';
import { getDefaultCard } from '~/features/cart/cart';
import { useCart } from '~/hooks/use-cart';
import {
  useLawnSubscriptionRenewal,
  useSimulateRenewal,
} from '~/features/subscriptions/subscription-actions';
import { centsToCurrency } from '~/utils/numbers';
import { toMonthAndYear } from '~/utils/dates';
import { captureException } from '~/utils/exception-tracking';
import { useSession } from '~/features/session/session-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import CouponForm from '~/components/cart/cart-summary/coupon-form';
import { useUpdatePaymentMethod } from '~/features/payment-methods/payment-method-actions';
import { parseStripeApiError } from '~/features/stripe';
import { useLawn } from '~/features/lawns/lawn-actions';
import { safeLazy } from '~/utils/safe-lazy';

import styles from '~/components/modals/modal.module.scss';

const StripeProvider = safeLazy(
  () => import('~/components/form/stripe-provider')
);

const pages = {
  UPDATE_PAYMENT: 'UPDATE_PAYMENT',
  CONFIRM_CHARGE: 'CONFIRM_CHARGE',
};

const EarlyRenewalModal = ({
  onClose = () => {},
  isOpen,
  subscriptionUuid,
}) => {
  const [isPurchasing, setIsPurchasing] = useState(false);

  return (
    <Modal
      onClose={onClose}
      allowClose={!isPurchasing}
      isOpen={isOpen}
      className={styles.authFailModal}
    >
      <EarlyRenewalModalContent
        subscriptionUuid={subscriptionUuid}
        onClose={onClose}
        setIsPurchasing={setIsPurchasing}
        isPurchasing={isPurchasing}
      />
    </Modal>
  );
};

export default EarlyRenewalModal;

const EarlyRenewalModalContent = ({
  subscriptionUuid,
  onClose,
  setIsPurchasing,
  isPurchasing,
}) => {
  const { showFlashMessage } = useFlashMessage();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const { lawn } = useLawn();

  // TODO: this endpoint is no longer used and we would need to get specific cart/child carts for early renewal if/when this is revived
  const { purchases } = useSimulateRenewal(subscriptionUuid);
  const [page, setPage] = useState(pages.CONFIRM_CHARGE);

  const { updatePaymentMethod } = useUpdatePaymentMethod();

  // TODO: commenting out until we're ready to add coupons in this flow
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);
  const [couponErrorMessage, setCouponErrorMessage] = useState('');

  // Renewal-specific states
  const [couponCode, setCouponCode] = useState('');

  // TODO: this has gone away w/ the auth fail modal updates, we'd need to refactor this if/when we bring back the early renewal modal
  const {
    isLoading: isLoadingRenewal,
    subscriptionRenewal,
    renewSubscription,
    applyCoupon,
    error,
  } = useLawnSubscriptionRenewal();
  const { defaultCard } = useCart();

  const creditCardSubmitErrorHandler = (err, msg) => {
    showFlashMessage(msg, 'error');

    if (err.detail && err.detail.errorType === 'StripeError') {
      return parseStripeApiError(err);
    } else {
      return err;
    }
  };

  const refetchQueries = () =>
    Promise.all([
      queryClient.refetchQueries(['lawn']),
      queryClient.refetchQueries(['subscriptions', user.uuid]),
      queryClient.refetchQueries(['subscriptionsForLawn', lawn.uuid]),
      queryClient.refetchQueries(['shipments']),
      queryClient.refetchQueries(['purchaseOrderAndShipmentHistory']),
    ]);

  const handleCreditCardUpdate = async (paymentMethodId) => {
    try {
      await updatePaymentMethod(paymentMethodId);

      showFlashMessage('Your credit card has been updated.', 'success');

      setPage(pages.CONFIRM_CHARGE);
    } catch (err) {
      captureException(new EarlyRenewalError('handleCreditCardUpdate'), {
        extras: {
          error: JSON.stringify(err),
        },
      });

      return creditCardSubmitErrorHandler(
        err,
        'There was an issue adding your credit card.'
      );
    }
  };

  const handleRenewSubscription = async () => {
    setIsPurchasing(true);

    try {
      await renewSubscription(couponCode);
      await refetchQueries();

      onClose();
      showFlashMessage('Subscription renewed successfully!', 'success', 5000);
    } catch (err) {
      captureException(new EarlyRenewalError('handleRenewSubscription'), {
        extras: {
          error: JSON.stringify(err),
        },
      });

      let errMsg =
        'Could not complete your purchase at this time. Please try again later.';

      if (err.errorCode === 'failedPayment') {
        // If the payment failed, we'll refresh the data and show the auth fail CTA
        await refetchQueries();
        errMsg =
          'Woops, it looks like your payment failed. Please update your payment method.';
      }

      showFlashMessage(errMsg, 'error', 5000);
      onClose();
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRemoveCoupon = async () => {
    try {
      setIsRemovingCoupon(true);
      // This effectively 'removes' the coupon from our local state
      await applyCoupon();
      setCouponCode('');
      showFlashMessage('Coupon removed from cart.', 'success');
    } catch (err) {
      captureException(new EarlyRenewalError('handleRemoveCoupon'), {
        extras: {
          error: JSON.stringify(err),
        },
      });

      setCouponErrorMessage('There was an issue removing the coupon.');
    } finally {
      setIsRemovingCoupon(false);
    }
  };

  const cardOnFile = getDefaultCard(subscriptionRenewal, defaultCard);

  const renewalSubtotal = centsToCurrency(
    purchases?.purchases?.payments?.[1].total ?? 0
  );
  const renewalDate = toMonthAndYear(purchases?.purchases?.payments[1].date);

  if (page === pages.UPDATE_PAYMENT) {
    return (
      <>
        <h1 className={cx(styles.title, styles.authFailModalTitle)}>
          payment info
        </h1>
        <p className={styles.authFailError}>
          Please update your payment information
        </p>

        {cardOnFile && (
          <CreditCardBanner cardInfo={cardOnFile} variant="error" />
        )}
        <div className={styles.authFailFormContainer}>
          <Suspense fallback={null}>
            <StripeProvider mode="setup">
              <AddCreditCardForm onSubmit={handleCreditCardUpdate} />
            </StripeProvider>
          </Suspense>
        </div>
      </>
    );
  } else if (page === pages.CONFIRM_CHARGE) {
    return (
      <>
        <h1 className={cx(styles.title, styles.authFailModalTitle)}>
          confirm charge
        </h1>
        <p className={styles.authFailModalMessage}>
          Renew Smart Lawn Plan subscription
        </p>

        {error ? (
          <p className={styles.errorMessage}>
            {error} You can also{' '}
            <button
              className={styles.errorMessageLink}
              onClick={() => setPage(pages.UPDATE_PAYMENT)}
            >
              try a different payment method
            </button>
            .
          </p>
        ) : isLoadingRenewal ? (
          <div className={styles.loadingIndicator}>
            <LoadingIndicator />
          </div>
        ) : (
          <>
            <div className={styles.miniCartContainer}>
              {/* TODO: If we revive this component, we'll need to use the new <MiniCartItemList /> and create a customer <CartSummary /> in place of <MiniCart /> */}
              {/* <MiniCart
                cart={subscriptionRenewal}
                showCouponLine={false}
                purchases={subscriptionRenewal?.purchases}
                showSeasonalShipping={false}
              /> */}
              {couponCode ? (
                <Button
                  variant="outline"
                  disabled={Boolean(isPurchasing || isRemovingCoupon)}
                  isLoading={isRemovingCoupon}
                  fullWidth
                  onClick={handleRemoveCoupon}
                >
                  remove coupon
                </Button>
              ) : (
                <CouponForm
                  addCouponOverride={async (coupon) => {
                    try {
                      setCouponErrorMessage('');
                      await applyCoupon(coupon);
                      setCouponCode(coupon);
                    } catch (err) {
                      setCouponErrorMessage('That coupon code is not valid.');

                      captureException(
                        new EarlyRenewalError('addCouponOverride'),
                        {
                          extras: {
                            error: JSON.stringify(err),
                            coupon,
                          },
                        }
                      );
                    }
                  }}
                  onCouponError={setCouponErrorMessage}
                />
              )}
              {couponErrorMessage && (
                <div className={styles.couponErrorMessage}>
                  {couponErrorMessage}
                </div>
              )}
            </div>
            <div className={styles.renewalModalFooter}>
              <div className={styles.termsContainer}>
                <TermsConsent
                  textBlocks={[
                    `Your next full season lawn plan (${renewalSubtotal} + tax) will be charged to your credit card on ${renewalDate} and annually thereafter.`,
                  ]}
                  buttonText="confirm and pay"
                />
              </div>

              <Button
                disabled={Boolean(error || isPurchasing)}
                isLoading={isPurchasing}
                fullWidth
                onClick={handleRenewSubscription}
                data-heap-id="renew-early-confirm-button"
              >
                confirm and pay
              </Button>
            </div>
          </>
        )}
      </>
    );
  }
};

class EarlyRenewalError extends Error {
  constructor(message) {
    super(message);
    this.name = 'EarlyRenewalError';
  }
}
